import { eachDayOfInterval, endOfMonth, endOfYear, isWeekend, startOfMonth, startOfYear } from 'date-fns';
import { User } from '../models/user.model';

export interface CapacityPeriod {
  year: number;
  month?: number;
}

interface HolidaysLike {
  isHoliday(date: Date): unknown;
}

interface HolidaysModule {
  default?: new (country: string, state?: string, region?: string) => HolidaysLike;
}

let holidaysInstance: HolidaysLike | null = null;
let holidaysPromise: Promise<HolidaysLike> | null = null;
let poaFallbackLogged = false;

function resolvePeriodBounds(period: CapacityPeriod) {
  const { year, month } = period;
  const baseDate = month
    ? startOfMonth(new Date(year, month - 1, 1))
    : startOfYear(new Date(year, 0, 1));

  return {
    start: baseDate,
    end: month ? endOfMonth(baseDate) : endOfYear(baseDate),
  };
}

function resolveHolidaysConstructor(module: HolidaysModule) {
  const Holidays = module.default;

  if (!Holidays) {
    throw new Error('date-holidays module missing default export.');
  }

  return Holidays;
}

function createHolidaysInstance(
  Holidays: new (country: string, state?: string, region?: string) => HolidaysLike
): HolidaysLike {
  try {
    return new Holidays('BR', 'RS', 'POA');
  } catch (error) {
    if (!poaFallbackLogged) {
      console.warn('date-holidays: POA nao suportado, usando fallback BR+RS.', error);
      poaFallbackLogged = true;
    }

    return new Holidays('BR', 'RS');
  }
}

async function loadHolidays(): Promise<HolidaysLike> {
  const holidaysModule = await import('date-holidays');
  const Holidays = resolveHolidaysConstructor(holidaysModule as HolidaysModule);
  return createHolidaysInstance(Holidays);
}

export async function ensureHolidays(): Promise<void> {
  if (!holidaysPromise) {
    holidaysPromise = loadHolidays().then(instance => {
      holidaysInstance = instance;
      return instance;
    });
  }

  await holidaysPromise;
}

function getHolidays(): HolidaysLike {
  if (!holidaysInstance) {
    throw new Error('Holiday calendar not initialized. Call ensureHolidays() before using capacity helpers.');
  }

  return holidaysInstance;
}

export function getBusinessDaysInPeriod(period: CapacityPeriod): Date[] {
  const holidays = getHolidays();
  const { start, end } = resolvePeriodBounds(period);

  return eachDayOfInterval({ start, end }).filter(date => !isWeekend(date) && !holidays.isHoliday(date));
}

export function countBusinessDaysInPeriod(period: CapacityPeriod): number {
  return getBusinessDaysInPeriod(period).length;
}

export function getUserPeriodCapacity(user: User, period: CapacityPeriod): number {
  if (!user.isMaintenanceTeam) {
    return 0;
  }

  const dailyAvailableHours = Number(user.dailyAvailableHours ?? 0);
  if (dailyAvailableHours <= 0) {
    return 0;
  }

  return countBusinessDaysInPeriod(period) * dailyAvailableHours;
}

export function getTotalMaintenanceCapacity(users: User[], period: CapacityPeriod): number {
  return users.reduce((total, user) => total + getUserPeriodCapacity(user, period), 0);
}
